import agentWebSocket from "@app/config/agentWebSocket";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useClearAgentSession, useUploadSessionDocument } from "@app/queries/agent";
import type { AgentChatMode } from "@app/state/types";
import { useAuth0 } from "@auth0/auth0-react";
import { buildUploadForm } from "@lib/agent/chatAttachments";
import { confirm } from "@lib/common/components/modals/modals";
import AgentChat from "@lib/experiment/agent/AgentChat/AgentChat";
import { useAgentChatContext } from "@lib/experiment/agent/AgentChatContext";
import ChatSessionUserFeedback from "@lib/experiment/agent/ChatSessionUserFeedback/ChatSessionUserFeedback";
import type { ChatBoxSubmitPayload } from "@lib/experiment/agent/ExperimentDesignChatBox/ChatBox";
import { notifications } from "@lib/notification/notification";
import { Text } from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export type ExperimentAgentChatVariant = "draft" | "experiment";

// Files picked on the landing page (InspirationalChatBox) ride along via React Router history state —
// File objects survive structured clone. Returns only the entries that are still real File objects.
function readHandoffFiles(locationState: unknown): File[] {
  const state = locationState as { files?: unknown } | null;
  if (!state?.files || !Array.isArray(state.files)) {
    return [];
  }
  return state.files.filter((f): f is File => f instanceof File);
}

type ExperimentAgentChatProps = {
  chatMode: AgentChatMode;
  setChatMode: (mode: AgentChatMode) => void;
  toggle: (value: boolean) => void;
  variant?: ExperimentAgentChatVariant;
};

export default function ExperimentAgentChat({
  chatMode,
  toggle,
  setChatMode,
  variant = "experiment"
}: ExperimentAgentChatProps) {
  const {
    experimentId,
    teamId,
    messages,
    liveExtras,
    isStreaming,
    isStopping,
    connectionState,
    send,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    hasRestoredHistory,
    agentSessionId,
    committedMessageCount,
    committedDocuments
  } = useAgentChatContext();
  const posthog = usePostHog();
  const clearMutation = useClearAgentSession();
  // Draft files are held locally and uploaded only on send, so abandoning a draft leaves nothing
  // attached to the session.
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  const uploadMutation = useUploadSessionDocument();
  // `mutateAsync` is referentially stable across renders (unlike the mutation object itself),
  // so the effects/callbacks below can depend on just this without re-registering every render.
  const uploadDocument = uploadMutation.mutateAsync;

  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuerySentRef = useRef(false);
  const isDraft = variant === "draft";

  // Handed-off files exist only for a draft; the agent session doesn't exist on the landing page,
  // so they're uploaded here once the draft (and its session) does. The upload attaches them to the
  // session, so the agent picks them up server-side on the first turn — nothing travels on the WS prompt.
  const initialFilesRef = useRef<File[]>(isDraft ? readHandoffFiles(location.state) : []);

  // This component instance is reused across navigation between drafts/experiments (no `key` at the
  // render site), so per-draft state must be reset when the experiment changes: clear stale draft
  // chips, release the one-shot query handoff so a new draft's `?query=` is honored, and re-read the
  // files handed off to the new draft. Runs after the refs/location above are declared.
  useEffect(() => {
    setDraftFiles([]);
    initialQuerySentRef.current = false;
    initialFilesRef.current = isDraft ? readHandoffFiles(location.state) : [];

    // current location/isDraft are read intentionally, not tracked (adding them would wrongly reset
    // mid-draft when the URL's `?query=` is stripped).
  }, [experimentId]);

  // Draft entry only: forward `?query=...` (and any handed-off files) as the first user
  // message, then strip the query from the URL/history so reload/back-nav doesn't resend.
  // Wait for the socket *and* the history fetch to resolve before deciding — otherwise a failed
  // history fetch would burn the one-shot flag and lose the query param across a retry. Also wait
  // for any in-flight stream, since `send` no-ops while streaming and the prompt would be dropped.
  useEffect(() => {
    if (!isDraft) {
      return;
    }
    if (
      connectionState !== "connected" ||
      isHistoryLoading ||
      historyError ||
      isStreaming ||
      initialQuerySentRef.current
    ) {
      return;
    }
    initialQuerySentRef.current = true;
    if (hasRestoredHistory) {
      return;
    }

    const initialQuery = searchParams.get("query");
    if (!initialQuery) {
      return;
    }

    const files = initialFilesRef.current;
    initialFilesRef.current = [];

    // The upload window can be seconds (large files), and `initialQuerySentRef` only guards this
    // mount. If the component remounts mid-upload (StrictMode, a route/key change), a fresh mount
    // would re-run this bootstrap while *this* mount's async is still in flight — sending the same
    // first prompt twice. Aborting on cleanup cancels the in-flight upload and skips the send, so
    // only the surviving mount dispatches the query.
    const controller = new AbortController();
    let sent = false;
    void (async () => {
      // Upload handed-off files first so they're attached to the session before the first turn —
      // the agent receives the session's document set server-side, no handles travel with the
      // prompt. Unlike the compose path, a failed upload here is best-effort and must NOT block the
      // first message; the user can re-attach in-chat.
      let filenames: string[] = [];
      if (files.length > 0) {
        try {
          const result = await uploadDocument({
            params: { path: { experiment_id: experimentId } },
            body: buildUploadForm(files),
            signal: controller.signal
          });
          filenames = result.documents.map(d => d.filename);
        } catch (error) {
          // An abort isn't a user-facing failure — the turn is being torn down anyway.
          if (controller.signal.aborted) {
            return;
          }
          const detail = error instanceof Error ? error.message : undefined;
          notifications.error("Couldn't attach the selected documents to this chat.", detail);
        }
      }
      if (controller.signal.aborted) {
        return;
      }
      // Best-effort one-shot dispatch. The socket was connected when this effect ran, so `send`
      // only fails if it dropped during the upload window — the same rare race the compose path
      // accepts. We don't retry it: leaving `?query=` in the URL to retry could resurface this
      // landing query after the user has already given up waiting and sent their own first message.
      send(initialQuery, { filenames });
      sent = true;

      // Strip `?query=` only after dispatching. Stripping it up front would lose the message if the
      // user reloads during the (possibly slow) file-upload window; keeping it until now means a
      // reload in that window simply re-runs this handoff.
      const next = new URLSearchParams(searchParams);
      next.delete("query");
      void navigate(
        { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
        { replace: true }
      );
    })();

    return () => {
      controller.abort();
      // Release the one-shot claim if we tore down before dispatching. The ref persists across a
      // StrictMode remount (and a genuine remount makes a fresh one), so without this the surviving
      // mount would see the claim still set and never send the query.
      if (!sent) {
        initialQuerySentRef.current = false;
      }
    };
  }, [
    isDraft,
    connectionState,
    isHistoryLoading,
    historyError,
    isStreaming,
    hasRestoredHistory,
    searchParams,
    send,
    navigate,
    location.pathname,
    experimentId,
    uploadDocument
  ]);

  // Cancels an in-flight compose upload when the component unmounts or the experiment changes (this
  // instance is reused across navigation). Without the experiment-change abort, an upload started on
  // the previous chat would finish against its session and its prompt could dispatch to the one the
  // user switched to. The aborted request is swallowed by the `signal.aborted` guard in the handler.
  const composeUploadControllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => composeUploadControllerRef.current?.abort(), [experimentId]);

  const handleComposerSubmit = useCallback(
    async (message: string, payload: ChatBoxSubmitPayload): Promise<boolean> => {
      const files = payload.files ?? [];
      if (files.length === 0) {
        // Nothing to commit first, so dispatch directly; `send` reports whether the socket was ready.
        return send(message);
      }

      // Don't upload unless the turn can actually be sent. Uploading commits the documents to the
      // session, so committing them and then dropping the prompt on a not-ready socket would leave
      // them attached with no message — the invisible-attachment failure this change prevents.
      if (connectionState !== "connected") {
        notifications.error("Not connected", "Reconnecting to the assistant — try sending again in a moment.");
        return false;
      }

      const controller = new AbortController();
      composeUploadControllerRef.current = controller;
      try {
        const result = await uploadDocument({
          params: { path: { experiment_id: experimentId } },
          body: buildUploadForm(files),
          signal: controller.signal
        });
        // The agent reads the session's document set server-side; the prompt only carries the
        // filenames so the sent message can render its attachment chips.
        const filenames = result.documents.map(d => d.filename);
        if (!send(message, { filenames })) {
          // The socket dropped during the upload, so the prompt didn't dispatch. The documents are
          // already on the session — drop them from the draft so a retry does NOT re-upload them
          // (the backend would reject them as already attached), but keep the typed message so the
          // user can resend it. That retry sends text only, and the committed documents ride it.
          setDraftFiles([]);
          notifications.error(
            "Message not sent",
            "Reconnecting — your documents were attached. Send your message again in a moment."
          );
          return false;
        }
        return true;
      } catch (error) {
        // An abort means the tree is being torn down — nothing to surface, and the draft is gone.
        if (controller.signal.aborted) {
          return false;
        }
        // The backend is authoritative on rejections (duplicate name or content, cumulative cap,
        // size) and returns a message that already names the offending file, so show it verbatim.
        // Returning false keeps the draft so the user can fix and resend without retyping.
        const detail = error instanceof Error ? error.message : undefined;
        notifications.error("Couldn't attach document", detail);
        return false;
      } finally {
        if (composeUploadControllerRef.current === controller) {
          composeUploadControllerRef.current = null;
        }
      }
    },
    [send, uploadDocument, experimentId, connectionState]
  );

  const handleClear = useCallback(async () => {
    // Optimistic: clear the UI immediately. The DELETE drops the DB row + cascade
    // messages; closing the WS forces the consumer's `disconnect` handler to evict
    // the cached Strands Agent so the next prompt rebuilds fresh state. We then
    // reconnect so the chat is usable again without a manual reload.
    clearLocal();
    agentWebSocket.close();
    try {
      await clearMutation.mutateAsync({ params: { path: { experiment_id: experimentId } } });
      // The session's documents are gone with the cascade, so clear the draft chips too.
      setDraftFiles([]);
    } catch {
      // Notification is raised by the mutation's onError. Refetch server history to restore
      // the conversation the optimistic clear wiped from the UI.
      void refetchHistory();
    }
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
  }, [clearLocal, clearMutation, experimentId, teamId, refetchHistory, getAccessTokenSilently]);

  const onClear = useCallback(() => {
    const subject = isDraft ? "draft" : "experiment";
    confirm({
      title: "Clear conversation",
      danger: true,
      labels: { confirm: "Clear" },
      children: (
        <Text size="sm">This permanently removes the chat history and any attached files for this {subject}.</Text>
      ),
      onConfirm: () => {
        posthog.capture(posthogEvents.experiment_agent.clear_conversation_confirmed_click);
        void handleClear();
      },
      onCancel: () => posthog.capture(posthogEvents.experiment_agent.clear_conversation_cancelled_click)
    });
  }, [handleClear, isDraft, posthog]);

  // Disabled while an upload is in flight so a DELETE can't race the atomic compose POST. Clearing
  // stays available when the session has committed documents even with no messages, so a user can
  // still drop attachments from an otherwise-empty chat.
  const clearDisabled =
    isStreaming ||
    isHistoryLoading ||
    clearMutation.isPending ||
    uploadMutation.isPending ||
    (messages.length === 0 && committedDocuments.count === 0);

  const userFeedback = agentSessionId ? (
    <ChatSessionUserFeedback
      agentSessionId={agentSessionId}
      disabled={isStreaming}
      messageCount={committedMessageCount}
    />
  ) : null;

  return (
    <AgentChat
      messages={messages}
      extras={liveExtras}
      isStreaming={isStreaming}
      isStopping={isStopping}
      connectionState={connectionState}
      send={send}
      onComposerSubmit={handleComposerSubmit}
      selectedFiles={draftFiles}
      onFilesChange={setDraftFiles}
      committedDocuments={committedDocuments}
      stop={stop}
      isHistoryLoading={isHistoryLoading}
      historyError={historyError}
      onRetryHistory={() => void refetchHistory()}
      onClear={onClear}
      disabledClear={clearDisabled}
      userFeedback={userFeedback}
      variant={variant}
      toggle={toggle}
      chatMode={chatMode}
      setChatMode={setChatMode}
    />
  );
}
